import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBook, FaBars, FaTimes, FaClipboardList, FaBookOpen, FaChartLine, FaChevronDown, FaStar, FaRocket, FaHistory, FaSearch } from 'react-icons/fa';
import SearchTool from '../SearchTool';

function LearningLayout({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [cursosOpen, setCursosOpen] = useState(false);
    const [searchPopupOpen, setSearchPopupOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const levelItems = [
        { id: 'intro', label: 'Introdução', sectionId: 'intro-section', icon: FaBook, iconColor: 'text-purple-600' },
        { id: 'nivel1', label: 'Nível 1 - PRESENTE', sectionId: 'nivel1-section', icon: FaStar, iconColor: 'text-emerald-500' },
        { id: 'nivel2', label: 'Nível 2 - FUTURO', sectionId: 'nivel2-section', icon: FaRocket, iconColor: 'text-orange-500' },
        { id: 'nivel3', label: 'Nível 3 - PASSADO', sectionId: 'nivel3-section', icon: FaHistory, iconColor: 'text-blue-600' },
    ];

    const progressItem = { id: 'progress', label: 'Progresso', path: '/progress', icon: FaChartLine, iconColor: 'text-cyan-500', color: 'from-cyan-500 to-blue-500' };

    const courses = [
        {
            id: 'basico',
            label: 'Básico',
            description: 'Present Tense',
            url: 'https://www.udemy.com/course/curso-de-ingles-intermediario-2-lane-antunes/?referralCode=EB310BD715B67485024A',
            color: 'emerald',
        },
        {
            id: 'intermediario1',
            label: 'Intermediário 1',
            description: 'Future Tense',
            url: 'https://www.udemy.com/course/curso-de-ingles-intermediario-90-em-ingles-com-legendas/?referralCode=CAADBF2564957289B935',
            color: 'orange',
        },
        {
            id: 'intermediario2',
            label: 'Intermediário 2',
            description: 'Past Tense',
            url: 'https://www.udemy.com/course/curso-de-ingles-intermediario-2-lane-antunes/?referralCode=EB310BD715B67485024A',
            color: 'blue',
        },
    ];

    const isActive = (path) => location.pathname === path;

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setSearchPopupOpen(false);
            }
        };

        if (searchPopupOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [searchPopupOpen]);

    return (
        <div className='flex h-screen w-screen bg-gradient-to-br from-violet-50 via-indigo-50 to-purple-50 overflow-hidden'>
            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed left-0 top-0 h-screen w-72 bg-white/95 backdrop-blur-md border-r border-gray-200 shadow-lg transition-all duration-300 z-50 m-0 overflow-hidden ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                } md:static md:translate-x-0 flex flex-col`}
            >
                <div className="p-6 flex-shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800">Exercícios</h3>
                    </div>
                </div>

                <nav className="space-y-2 flex-1 overflow-y-auto px-4">
                        {/* Level Items */}
                        {levelItems.map((item) => {
                            const Icon = item.icon;
                            const isOnProgressPage = location.pathname === '/progress';
                            const isOnQuizPage = location.pathname.startsWith('/quiz/');
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => {
                                        if (isOnProgressPage || isOnQuizPage) {
                                            // Navigate to grammar page with hash
                                            navigate(`/grammar#${item.sectionId}`);
                                        } else {
                                            // Scroll to section on current page
                                            const element = document.getElementById(item.sectionId);
                                            if (element) {
                                                element.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }
                                        setSidebarOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                                >
                                    <Icon className={`text-lg flex-shrink-0 ${item.iconColor}`} />
                                    <span>{item.label}</span>
                                </button>
                            );
                        })}

                        {/* Divider */}
                        <div className="my-4 border-t border-gray-300"></div>

                        {/* Search Button */}
                        <button
                            onClick={() => setSearchPopupOpen(true)}
                            className="w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                        >
                            <FaSearch className="text-lg flex-shrink-0 text-blue-600" />
                            <span>Procurar Quizzes</span>
                        </button>

                        {/* Progress Item */}
                        {(() => {
                            const Icon = progressItem.icon;
                            const isProgressActive = isActive(progressItem.path);
                            return (
                                <Link
                                    to={progressItem.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        isProgressActive
                                            ? `bg-gradient-to-r ${progressItem.color} text-white shadow-md`
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <Icon className={`text-lg flex-shrink-0 ${isProgressActive ? 'text-white' : progressItem.iconColor}`} />
                                    <span>{progressItem.label}</span>
                                </Link>
                            );
                        })()}

                        {/* Cursos Section */}
                        <button
                            onClick={() => setCursosOpen(!cursosOpen)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-all duration-300"
                        >
                            <div className="flex items-center gap-3">
                                <FaBook className="text-lg text-blue-600 flex-shrink-0" />
                                <span>Cursos</span>
                            </div>
                            <FaChevronDown
                                className={`text-sm transition-transform duration-300 ${cursosOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {/* Courses List */}
                        {cursosOpen && (
                            <div className="space-y-2 pl-4">
                                {courses.map((course) => (
                                    <a
                                        key={course.id}
                                        href={course.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex flex-col px-4 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
                                            course.color === 'emerald'
                                                ? 'text-emerald-700 hover:bg-emerald-100'
                                                : course.color === 'orange'
                                                ? 'text-orange-700 hover:bg-orange-100'
                                                : 'text-blue-700 hover:bg-blue-100'
                                        }`}
                                    >
                                        <span>{course.label}</span>
                                        <span className="text-xs font-normal opacity-75">{course.description}</span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </nav>
            </aside>

            {/* Main Content */}
            <div className='flex-1 overflow-auto flex flex-col'>
                {/* Mobile Menu Button */}
                <div className='sticky top-0 z-30 md:hidden bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-md p-4'>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
                    >
                        {sidebarOpen ? (
                            <FaTimes className='text-xl text-gray-700' />
                        ) : (
                            <FaBars className='text-xl text-gray-700' />
                        )}
                    </button>
                </div>

                {/* Content */}
                {children}
            </div>

            {/* Search Popup */}
            {searchPopupOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center pt-40">
                    {/* Overlay */}
                    <div
                        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                        onClick={() => setSearchPopupOpen(false)}
                    />

                    {/* Popup */}
                    <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl z-50">
                        {/* Search Bar with Close Button */}
                        <div className="flex items-center gap-2 p-6">
                            <div className="flex-1">
                                <SearchTool />
                            </div>
                            <button
                                onClick={() => setSearchPopupOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                            >
                                <FaTimes className="text-xl text-gray-700" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LearningLayout;
